import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { getWorkspace } from "../lib/api";
import { useAuthUser } from "../lib/useAuthUser";
import { isStandalone, openManagedWindows, popupHelp, POPUP_TOAST_DURATION } from "../lib/pwa";

const SESSION_KEY = "tv_ws_launched"; // once launched/dismissed this session, stop nagging

/**
 * Installed-PWA workspace launcher. When the app opens (standalone) and the
 * signed-in user has a saved workspace, a prominent brutalist banner offers to
 * open every workspace tool as its own tiled window. We can't auto-open on load
 * (browsers block pop-ups without a user gesture), so this is a one-click
 * launch surface that explains the pop-up permission if needed.
 */
export default function WorkspaceLauncher() {
  const loc = useLocation();
  const { user } = useAuthUser();
  const [windows, setWindows] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });

  const standalone = isStandalone();
  // Only on the home index (the PWA start_url), in standalone, for logged-in users.
  const eligible = standalone && (loc.pathname === "/" || loc.pathname === "/index.html") && !!user;

  useEffect(() => {
    if (!eligible) return undefined;
    let alive = true;
    getWorkspace()
      .then((w) => alive && setWindows(w?.windows || []))
      .catch(() => alive && setWindows([]));
    return () => {
      alive = false;
    };
  }, [eligible]);

  if (!eligible || dismissed || windows.length === 0) return null;

  const markDone = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* storage blocked */
    }
  };

  const launch = async () => {
    const opened = await openManagedWindows(
      windows.map((w, i) => ({ url: w.url, name: `tv_ws_${i}` })),
    );
    const n = windows.length;
    if (opened === 0) {
      toast.error(popupHelp(), { duration: POPUP_TOAST_DURATION });
      return;
    }
    if (opened < n) {
      toast.warning(`Opened ${opened} of ${n}. ${popupHelp()}`, { duration: POPUP_TOAST_DURATION });
    } else {
      toast.success(`opened workspace · ${n} windows`);
    }
    markDone();
  };

  return (
    <div
      className="bg-white text-black border-b border-black"
      data-testid="workspace-launcher"
    >
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <span className="text-[12px] font-bold whitespace-nowrap">
          {">"}_ your workspace
        </span>
        <span className="text-[12px] text-black/55 truncate flex-1 min-w-[120px]">
          {windows.map((w) => w.title).join(" · ")}
        </span>
        <button
          type="button"
          onClick={launch}
          className="px-3 py-1.5 text-[12px] font-bold border border-black bg-white hover:bg-[#1a73e8] hover:text-white hover:border-[#1a73e8] transition-colors whitespace-nowrap"
          data-testid="workspace-launcher-open"
        >
          ▶ open ({windows.length} windows)
        </button>
        <Link
          to="/workspace"
          onClick={markDone}
          className="text-[12px] whitespace-nowrap text-[#1a73e8] hover:underline"
          data-testid="workspace-launcher-edit"
        >
          edit
        </Link>
        <button
          type="button"
          onClick={markDone}
          className="text-[14px] leading-none text-black/50 hover:text-black"
          data-testid="workspace-launcher-dismiss"
          aria-label="dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
